import { useCallback, useEffect, useMemo, useState } from 'react';
import { vendorApiClient, type MeCapabilities, type VendorMembershipInfo } from './vendorApiClient';

interface CapabilitiesState {
  data: MeCapabilities | null;
  loading: boolean;
  error: string | null;
}

export interface UseCapabilities extends CapabilitiesState {
  /** The currently-selected vendor's id (a user may belong to several). */
  activeVendorId: string | null;
  /** Switch the active vendor (used by the shell switcher). */
  setActiveVendorId: (vendorId: string) => void;
  /** The active membership (role + capabilities for the active vendor). */
  activeMembership: VendorMembershipInfo | null;
  /** True if the active vendor grants `cap`. Cosmetic — the server re-checks. */
  has: (cap: string) => boolean;
  /** Re-fetch capabilities (e.g. after submitting an application). */
  refresh: () => void;
}

/**
 * Fetches the caller's vendor memberships + current application from
 * `/vendor/me/capabilities` and tracks an ACTIVE vendor. Drives Option A
 * routing (applicant vs member). Most users belong to one vendor
 * (auto-selected); multi-vendor users get a switcher in the shell.
 */
export function useCapabilities(): UseCapabilities {
  const [state, setState] = useState<CapabilitiesState>({ data: null, loading: true, error: null });
  const [activeVendorId, setActiveVendorId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    vendorApiClient
      .getCapabilities()
      .then((data) => {
        if (!alive) return;
        setState({ data, loading: false, error: null });
        // Keep the caller's last valid business selected across sessions.
        if (data.memberships.length > 0) {
          let remembered: string | null = null;
          try { remembered = window.localStorage.getItem('glp.vendor.activeVendorId'); } catch { /* storage is optional */ }
          setActiveVendorId((cur) => {
            const candidate = cur ?? remembered;
            return data.memberships.some((membership) => membership.vendorId === candidate) ? candidate : data.memberships[0].vendorId;
          });
        }
      })
      .catch((err: Error) => alive && setState({ data: null, loading: false, error: err.message }));
    return () => {
      alive = false;
    };
  }, [nonce]);

  const selectVendor = useCallback((vendorId: string) => {
    setActiveVendorId(vendorId);
    try { window.localStorage.setItem('glp.vendor.activeVendorId', vendorId); } catch { /* storage is optional */ }
  }, []);

  const activeMembership = useMemo(
    () => state.data?.memberships.find((m) => m.vendorId === activeVendorId) ?? null,
    [state.data, activeVendorId],
  );

  return {
    ...state,
    activeVendorId,
    setActiveVendorId: selectVendor,
    activeMembership,
    has: (cap: string) => activeMembership?.capabilities.includes(cap) ?? false,
    refresh,
  };
}
